// Global confirm dialog — replaces native window.confirm with a consistent
// frosted-glass modal that matches the rest of the Hub (zie o.a. Danger Zone
// in TeamPanel.jsx, ChangeRoleModal, Super Admin workspace delete).
//
// Usage:
//   const confirm = useConfirm();
//   const ok = await confirm({
//     title: "Chat verwijderen?",
//     body: "Dit kan niet ongedaan worden gemaakt.",
//     confirmLabel: "Verwijderen",
//     tone: "danger", // "danger" | "default"
//   });
//   if (!ok) return;
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";

const ConfirmContext = createContext(() => Promise.resolve(false));

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // {title, body, confirmLabel, cancelLabel, tone}
  const resolverRef = useRef(null);

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({
        title: opts?.title || "Weet je het zeker?",
        body: opts?.body || "",
        confirmLabel: opts?.confirmLabel || "Bevestigen",
        cancelLabel: opts?.cancelLabel || "Annuleren",
        tone: opts?.tone === "danger" ? "danger" : "default",
      });
    });
  }, []);

  const finish = useCallback((value) => {
    const r = resolverRef.current;
    resolverRef.current = null;
    setState(null);
    if (r) r(value);
  }, []);

  // ESC to cancel, Enter to confirm
  useEffect(() => {
    if (!state) return;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); finish(false); }
      else if (e.key === "Enter") { e.preventDefault(); finish(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state, finish]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div
          className="fixed inset-0 z-[200] bg-white/10 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => finish(false)}
          data-testid="confirm-dialog-backdrop"
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="confirm-dialog"
            role="alertdialog"
            aria-modal="true"
          >
            <h3 className="font-display text-xl font-medium text-[#1a1a1a]" data-testid="confirm-dialog-title">
              {state.title}
            </h3>
            {state.body && (
              <p className="text-sm text-gray-500 mt-2 whitespace-pre-line" data-testid="confirm-dialog-body">
                {state.body}
              </p>
            )}
            <div className="flex justify-end gap-2 mt-6">
              <Button
                onClick={() => finish(false)}
                data-testid="confirm-dialog-cancel"
                className="h-10 rounded-md bg-white text-[#1a1a1a] border border-gray-200 hover:bg-gray-50"
              >
                {state.cancelLabel}
              </Button>
              <Button
                onClick={() => finish(true)}
                data-testid="confirm-dialog-confirm"
                autoFocus
                className={
                  state.tone === "danger"
                    ? "h-10 rounded-md bg-red-600 text-white hover:bg-red-700"
                    : "h-10 rounded-md bg-slate-900 text-white hover:bg-slate-800"
                }
              >
                {state.confirmLabel}
              </Button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export const useConfirm = () => useContext(ConfirmContext);
